import {ReactNode} from 'react';

export enum AppealStatus {
    SUBMITTED = 'SUBMITTED',
    IN_PROGRESS = 'IN_PROGRESS',
    PROCESSED = 'PROCESSED',
    RESOLVED = 'RESOLVED',
    REJECTED = 'REJECTED',
}

export enum AppealSlaIndicator {
    GREEN = 'GREEN',
    YELLOW = 'YELLOW',
    RED = 'RED',
}

export enum AppealExportFormat {
    CSV = 'CSV',
    EXCEL = 'EXCEL',
    JSON = 'JSON',
}

export interface AppealTheme {
    code: string;
    name: string;
}

export enum ComplaintsAccessors {
    id = 'id',
    theme = 'theme',
    status = 'status',
    createdAt = 'createdAt',
    assigneeName = 'assigneeName',
    branchName = 'branchName',
    store = 'storeName',
    markingCode = 'markingCode',
    codeNotFound = 'codeNotFound',
    slaIndicator = 'slaIndicator',
}

export interface AppealRegistryItem {
    id: number;
    createdAt: string;
    theme: string;
    status: AppealStatus;
    source?: string | null;
    assignedTo?: number | null;
    assigneeName?: string | null;
    branchId?: number | null;
    branchName?: string | null;
    markingCode?: string | null;
    codeNotFound?: boolean;
    slaDueAt?: string | null;
    slaIndicator?: AppealSlaIndicator | null;
    storeName?: string | null;
    storeAddress?: string | null;
}

export interface AppealsRegistryResponse {
    data: AppealRegistryItem[];
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
}

export interface AppealsRegistryRequest {
    paging: {
        pageNumber: number;
        pageSize: number;
    };
    sortings?: Array<{
        id: string;
        desc: boolean;
    }>;
    themes?: string[];
    statuses?: AppealStatus[];
    dateFrom?: string;
    dateTo?: string;
    assignedTo?: number;
    branchId?: number;
    codeOwnerId?: number;
    store?: string;
    markingCode?: string;
    slaOverdue?: boolean;
    codeNotFound?: boolean;
}

export interface NkBranch {
    id: number;
    name: string;
    region?: string | null;
    city?: string | null;
    geoBounds?: string | null;
    isActive?: boolean;
    employeeCount?: number;
    createdAt?: string;
}

export interface NkBranchRequest {
    name: string;
    region: string;
    city?: string;
    geoBounds?: string;
    isActive?: boolean;
}

export interface NkEmployee {
    userId: number;
    username?: string;
    firstName?: string;
    middleName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    isBlocked?: boolean;
    role?: NkEmployeeRole;
    branches?: NkBranch[];
    themeCodes?: string[];
}

export type NkEmployeeRole = 'EMPLOYEE' | 'ADMINISTRATOR';

export interface NkEmployeeCreateRequest {
    registrationRequest: {
        email: string;
        phone: string;
        type: 'EMPLOYEE';
        first_name: string;
        middle_name?: string;
        last_name: string;
    };
    role: NkEmployeeRole;
}

interface NkEmployeeFieldPatch {
    value: string;
    reset: boolean;
}

export interface NkEmployeeUpdateRequest {
    user: {
        phone: NkEmployeeFieldPatch;
        firstName: NkEmployeeFieldPatch;
        middleName: NkEmployeeFieldPatch;
        lastName: NkEmployeeFieldPatch;
    };
    role: NkEmployeeRole;
}

export interface Appeal {
    id: number;
    createdAt: string;
    updatedAt?: string | null;
    theme: string;
    description: string;
    fio?: string | null;
    phone?: string | null;
    storeName?: string | null;
    storeAddress?: string | null;
    productName?: string | null;
    username?: string | null;
    status: AppealStatus;
    response?: string | null;
    markingCode?: string | null;
    invalidCodeReason?: string | null;
    codeOwnerId?: number | null;
    assignedTo?: number | null;
    slaDueAt?: string | null;
    deviceId?: string | null;
    storeLat?: number | null;
    storeLng?: number | null;
    actUrl?: string | null;
    source?: string | null;
    createdBy?: number | null;
    branchId?: number | null;
}

export interface AppealHistoryItem {
    id: number;
    actorId?: number | null;
    actorName?: string | null;
    action?: string | null;
    fromStatus?: string | null;
    toStatus?: string | null;
    payload?: string | Record<string, unknown> | null;
    createdAt: string;
}

export interface AppealPhoto {
    id: number;
    fileName?: string | null;
    url?: string | null;
    createdAt?: string | null;
}

export interface AppealMarkingCode {
    code?: string | null;
    status?: string | null;
    productName?: string | null;
    owner?: string | null;
    notFound?: boolean;
}

export interface AppealCard {
    appeal: Appeal;
    assigneeName?: string | null;
    branchName?: string | null;
    slaIndicator?: AppealSlaIndicator | null;
    markingCode?: AppealMarkingCode | null;
    complaintPhotos?: AppealPhoto[];
    responsePhotos?: AppealPhoto[];
    history?: AppealHistoryItem[];
    canTake?: boolean;
    canComplete?: boolean;
    canAssign?: boolean;
}

export interface AppealAssignRequest {
    assignedTo?: number;
}

export interface NkAppealCreateRequest {
    theme: string;
    description: string;
    response: string;
    fio?: string;
    phone?: string;
    storeName?: string;
    storeAddress?: string;
    productName?: string;
    markingCode?: string;
    invalidCodeReason?: string;
    codeOwnerId?: number;
    branchId?: number;
    storeLat?: number;
    storeLng?: number;
    files?: File[];
}

export type ComplaintsFormattedRow = Record<ComplaintsAccessors, ReactNode> & {
    raw: AppealRegistryItem;
};
